import { gsap } from 'gsap'
import {
  LinearFilter,
  Material,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
  VideoTexture,
  Vector3,
  type Object3D,
} from 'three'

const VIDEO_ASPECT = 16 / 9

export class VideoScreenController {
  private screen: Mesh | null = null
  private standbyMaterial: MeshBasicMaterial | null = null
  private shutdownMaterial: MeshBasicMaterial | null = null
  private videoMaterial: MeshBasicMaterial | null = null
  private videoTexture: VideoTexture | null = null
  private videoElement: HTMLVideoElement | null = null
  private ownsVideoElement = false
  private powerDownTimeline: gsap.core.Timeline | null = null
  private powerDownResolve: (() => void) | null = null
  private poweredOff = false

  public bind(root: Object3D): Mesh {
    const candidate = root.getObjectByName('Television_VideoSurface')
    if (!(candidate instanceof Mesh)) {
      throw new Error('The GLB is missing the Television_VideoSurface mesh.')
    }

    candidate.geometry.computeBoundingBox()
    const bounds = candidate.geometry.boundingBox
    if (!bounds) throw new Error('The TV screen mesh has no usable bounds.')

    this.disposeMaterial(candidate.material)
    this.standbyMaterial = new MeshBasicMaterial({
      color: 0x07142b,
      toneMapped: false,
      fog: false,
    })
    this.standbyMaterial.name = 'VIDEO_TV_Screen_Standby'
    this.standbyMaterial.userData = {
      web_role: 'video',
      web_material_key: 'tv_screen',
      video_ready: true,
    }
    candidate.material = this.standbyMaterial
    candidate.userData.web_role = 'video-base'

    this.shutdownMaterial = new MeshBasicMaterial({
      color: 0xcde8ff,
      transparent: true,
      opacity: 1,
      toneMapped: false,
      fog: false,
    })
    this.shutdownMaterial.name = 'VIDEO_TV_Shutdown_Flash'

    // The imported object is a beveled box whose UVs also cover its sides.
    // Put the video on a single front-facing plane instead: two triangles,
    // complete 0-1 UVs, and FrontSide rendering toward the bed only.
    const size = bounds.getSize(new Vector3())
    const center = bounds.getCenter(new Vector3())
    const bevelInset = Math.min(size.x, size.y) * 0.013
    const availableWidth = size.x - bevelInset * 2
    const availableHeight = size.y - bevelInset * 2
    const widthLimited = availableWidth / availableHeight <= VIDEO_ASPECT
    const displayWidth = widthLimited ? availableWidth : availableHeight * VIDEO_ASPECT
    const displayHeight = widthLimited ? availableWidth / VIDEO_ASPECT : availableHeight
    const frontOffset = Math.max(size.z * 0.04, 0.0005)

    const displayGeometry = new PlaneGeometry(displayWidth, displayHeight)
    displayGeometry.name = 'Television_VideoDisplayFace_Geometry'
    this.screen = new Mesh(displayGeometry, this.standbyMaterial)
    this.screen.name = 'Television_VideoDisplayFace'
    this.screen.position.set(center.x, center.y, bounds.max.z + frontOffset)
    this.screen.userData.web_role = 'video'
    this.screen.userData.web_material_key = 'tv_screen'
    this.screen.userData.video_ready = true
    candidate.add(this.screen)
    return this.screen
  }

  public attachElement(
    video: HTMLVideoElement,
    ownsElement = false,
    autoplay = true,
  ): VideoTexture {
    if (!this.screen) throw new Error('Bind the TV screen before attaching video.')
    this.cancelPowerDown()
    this.releaseVideo()
    this.resetScreenVisual()

    this.videoElement = video
    this.ownsVideoElement = ownsElement
    video.muted = true
    video.defaultMuted = true
    video.loop = true
    video.autoplay = autoplay
    video.playsInline = true

    this.videoTexture = new VideoTexture(video)
    this.videoTexture.name = 'TVVideoTexture'
    this.videoTexture.colorSpace = SRGBColorSpace
    this.videoTexture.flipY = true
    this.videoTexture.minFilter = LinearFilter
    this.videoTexture.magFilter = LinearFilter

    this.videoMaterial = new MeshBasicMaterial({
      map: this.videoTexture,
      toneMapped: false,
      fog: false,
    })
    this.videoMaterial.name = 'VIDEO_TV_Screen_Runtime'
    this.videoMaterial.userData = { web_role: 'video', web_material_key: 'tv_screen' }
    this.screen.material = this.videoMaterial
    this.poweredOff = false
    return this.videoTexture
  }

  public async setSource(url: string, autoplay = true): Promise<HTMLVideoElement> {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.preload = 'auto'
    video.muted = true
    video.defaultMuted = true
    video.loop = true
    video.autoplay = autoplay
    video.playsInline = true
    video.src = url
    this.attachElement(video, true, autoplay)
    await this.waitForLoadedData(video)
    if (autoplay) await video.play()
    return video
  }

  public prepareSource(url: string): Promise<HTMLVideoElement> {
    return this.setSource(url, false)
  }

  public async play(): Promise<boolean> {
    if (!this.videoElement) return false
    await this.videoElement.play()
    return true
  }

  public powerDown(): Promise<void> {
    this.cancelPowerDown()
    const screen = this.screen
    const shutdownMaterial = this.shutdownMaterial
    if (!screen || !shutdownMaterial) {
      this.showStandby()
      return Promise.resolve()
    }

    this.videoElement?.pause()
    this.poweredOff = false
    this.resetScreenVisual()

    return new Promise((resolve) => {
      this.powerDownResolve = resolve
      this.powerDownTimeline = gsap.timeline({
        onComplete: () => {
          this.powerDownTimeline = null
          this.powerDownResolve = null
          this.releaseVideo()
          this.resetScreenVisual()
          this.poweredOff = true
          resolve()
        },
      })
      this.powerDownTimeline.to(screen.scale, {
        y: 0.025,
        duration: 0.34,
        ease: 'power3.in',
      })
      this.powerDownTimeline.add(() => {
        if (this.screen && this.shutdownMaterial) this.screen.material = this.shutdownMaterial
      }, '>-0.03')
      this.powerDownTimeline.to(screen.scale, {
        x: 0.035,
        duration: 0.24,
        ease: 'power4.in',
      })
      this.powerDownTimeline.to(shutdownMaterial, {
        opacity: 0,
        duration: 0.18,
        ease: 'power1.out',
      })
    })
  }

  public showStandby(): void {
    this.cancelPowerDown()
    this.releaseVideo()
    this.resetScreenVisual()
    if (this.screen && this.standbyMaterial) this.screen.material = this.standbyMaterial
    this.poweredOff = true
  }

  public get isBound(): boolean {
    return this.screen !== null
  }

  public get isPlaying(): boolean {
    return Boolean(this.videoElement && !this.videoElement.paused)
  }

  public get isPoweredOff(): boolean {
    return this.poweredOff
  }

  private waitForLoadedData(video: HTMLVideoElement): Promise<void> {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve()
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        cleanup()
        reject(new Error('The TV video did not finish preparing in time.'))
      }, 15000)
      const cleanup = (): void => {
        window.clearTimeout(timeout)
        video.removeEventListener('loadeddata', handleLoaded)
        video.removeEventListener('error', handleError)
      }
      const handleLoaded = (): void => {
        cleanup()
        resolve()
      }
      const handleError = (): void => {
        cleanup()
        reject(new Error('The TV video could not be decoded.'))
      }
      video.addEventListener('loadeddata', handleLoaded, { once: true })
      video.addEventListener('error', handleError, { once: true })
      video.load()
    })
  }

  private resetScreenVisual(): void {
    this.screen?.scale.set(1, 1, 1)
    if (this.shutdownMaterial) this.shutdownMaterial.opacity = 1
  }

  private cancelPowerDown(): void {
    if (!this.powerDownTimeline) return
    const resolve = this.powerDownResolve
    this.powerDownTimeline.kill()
    this.powerDownTimeline = null
    this.powerDownResolve = null
    this.resetScreenVisual()
    const material = this.videoMaterial ?? this.standbyMaterial
    if (this.screen && material) this.screen.material = material
    resolve?.()
  }

  private releaseVideo(): void {
    this.videoElement?.pause()
    if (this.ownsVideoElement && this.videoElement) {
      this.videoElement.removeAttribute('src')
      this.videoElement.load()
    }
    this.videoTexture?.dispose()
    this.videoMaterial?.dispose()
    if (this.screen && this.standbyMaterial) this.screen.material = this.standbyMaterial
    this.videoTexture = null
    this.videoMaterial = null
    this.videoElement = null
    this.ownsVideoElement = false
  }

  private disposeMaterial(material: Material | Material[]): void {
    const materials = Array.isArray(material) ? material : [material]
    materials.forEach((entry) => entry.dispose())
  }

  public dispose(): void {
    this.cancelPowerDown()
    this.releaseVideo()
    if (this.screen) {
      this.screen.removeFromParent()
      this.screen.geometry.dispose()
    }
    this.shutdownMaterial?.dispose()
    this.shutdownMaterial = null
    this.standbyMaterial?.dispose()
    this.standbyMaterial = null
    this.screen = null
  }
}
